import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, RefreshCw, X, Pencil, Trash2, Plus, Ban } from 'lucide-react';
import axios from 'axios';
import { WarrantyDropdown } from './forms/WarrantyDropdown';
import { DocumentExportActions } from './DocumentExportActions';
import { recalculateLineItem } from './lineItemCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  // Convert modal
  const [showConvert, setShowConvert] = useState(false);
  const [convertData, setConvertData] = useState({ bill_number: '', bill_date: '', payment_term_id: '', paid_amount: '' });
  const [convertLineWarranties, setConvertLineWarranties] = useState([]);
  const [convertLineItems, setConvertLineItems] = useState([]);
  const [convertSubmitting, setConvertSubmitting] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [showAddPO, setShowAddPO] = useState(false);
  const [additionalPoIds, setAdditionalPoIds] = useState([]);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes, ptRes, wRes, poRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/purchase-orders/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/payment-terms`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/warranties`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/purchase-orders`, getAuthHeaders()),
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
        setPaymentTerms(ptRes.data);
        setWarranties(wRes.data || []);
        setPurchaseOrders(poRes.data || []);
      } catch (err) { setError('Failed to load purchase order'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const getName = (list, id, field = 'name') => list.find(i => i.id === id)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const openConvertModal = () => {
    setConvertData({
      bill_number: '',
      bill_date: new Date().toISOString().split('T')[0],
      payment_term_id: doc.payment_term_id || '',
      paid_amount: '',
    });
    const items = (doc.line_items || []).map(li => ({ ...li }));
    setConvertLineItems(items);
    setConvertLineWarranties(items.map(li => ({
      warranty_id: li.warranty_id || '',
      warranty_name: li.warranty_name || ''
    })));
    setConvertError('');
    setAdditionalPoIds([]);
    setShowAddPO(false);
    setShowConvert(true);
  };

  const eligibleAdditionalPOs = purchaseOrders.filter(po =>
    po.id !== doc?.id &&
    po.vendor_id === doc?.vendor_id &&
    po.company_id === doc?.company_id &&
    !po.converted_to_bill &&
    po.status !== 'Voided'
  );

  const toggleAdditionalPO = (poId) => {
    const nextIds = additionalPoIds.includes(poId)
      ? additionalPoIds.filter(id => id !== poId)
      : [...additionalPoIds, poId];
    setAdditionalPoIds(nextIds);

    const includedPOs = [doc, ...purchaseOrders.filter(po => nextIds.includes(po.id))];
    const items = includedPOs.flatMap(po => (po.line_items || []).map(li => ({
      ...li,
      _source_po_id: po.id,
      _source_po_number: po.order_number,
    })));
    setConvertLineItems(items);
    setConvertLineWarranties(items.map(li => ({
      warranty_id: li.warranty_id || '',
      warranty_name: li.warranty_name || '',
    })));
  };

  const removeConvertLine = (idx) => {
    if (convertLineItems.length <= 1) return;
    setConvertLineItems(prev => prev.filter((_, i) => i !== idx));
    setConvertLineWarranties(prev => prev.filter((_, i) => i !== idx));
  };

  const updateConvertLine = (idx, field, value) => {
    setConvertLineItems(prev => {
      const u = [...prev];
      u[idx] = { ...u[idx], [field]: value };
      u[idx] = recalculateLineItem(u[idx], taxes, field);
      return u;
    });
  };

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    setConvertError('');
    if (!convertData.bill_number.trim()) { setConvertError('Bill Number is required'); return; }
    setConvertSubmitting(true);
    try {
      const paidAmt = parseFloat(convertData.paid_amount) || 0;
      const includedPOs = [doc, ...purchaseOrders.filter(po => additionalPoIds.includes(po.id))];
      const prefill = {
        source_po_id: doc.id,
        source_po_number: doc.order_number,
        source_po_ids: includedPOs.map(po => po.id),
        source_po_numbers: includedPOs.map(po => po.order_number).filter(Boolean),
        vendor_id: doc.vendor_id,
        bill_number: convertData.bill_number,
        bill_date: convertData.bill_date || null,
        state_of_supply: doc.state_of_supply,
        company_id: doc.company_id,
        payment_term_id: convertData.payment_term_id || null,
        line_items: convertLineItems.map((li, idx) => ({
          product_id: li.product_id,
          description: li.description || '',
          mrp: li.mrp || '',
          quantity: parseFloat(li.quantity) || 0,
          unit_id: li.unit_id || '',
          price_per_unit: parseFloat(li.price_per_unit) || 0,
          discount_percentage: parseFloat(li.discount_percentage) || 0,
          discount_amount: li.discount_amount || 0,
          tax_id: li.tax_id || '',
          tax_amount: li.tax_amount || 0,
          total_amount: li.total_amount || 0,
          warranty_id: convertLineWarranties[idx]?.warranty_id || '',
          warranty_name: convertLineWarranties[idx]?.warranty_name || '',
        })),
        payments: (doc.payments || []).map(p => ({ payment_type: p.payment_type, amount: p.amount, bank_detail_id: p.bank_detail_id || null })),
        description: doc.description || '',
        round_off: doc.round_off,
        paid_amount: paidAmt,
      };
      // Navigate to PB Create with prefilled state
      navigate('/admin/purchase/bills/new', { state: { fromPO: prefill } });
    } catch (err) { setConvertError(err.response?.data?.detail || 'Failed to navigate to purchase bill'); }
    finally { setConvertSubmitting(false); }
  };

  const conversionTotal = convertLineItems.reduce((sum, li) => sum + (Number(li.total_amount) || 0), 0);

  const handleVoid = async () => {
    const reason = voidReason.trim();
    if (!reason) {
      setVoidError('Please enter a reason for voiding this purchase order.');
      return;
    }
    setVoiding(true);
    setVoidError('');
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/purchase-orders/${id}/void`,
        { reason },
        getAuthHeaders()
      );
      setDoc(response.data);
      setShowVoidModal(false);
      setVoidReason('');
    } catch (err) {
      setVoidError(err.response?.data?.detail || 'Failed to void purchase order');
    } finally {
      setVoiding(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;
  const isVoided = doc.status === 'Voided';
  const exportConfig = {
    title: 'Purchase Order', number: doc.order_number,
    metadata: [
      { label: 'Order Number', value: doc.order_number || '-' }, { label: 'Order Date', value: formatDate(doc.order_date) },
      { label: 'Vendor', value: doc.vendor_name || '-' }, { label: 'Company', value: doc.company_name || '-' },
      { label: 'Due Date', value: formatDate(doc.due_date) }, { label: 'Status', value: doc.status || (doc.converted_to_bill ? 'Converted' : 'Open') },
      { label: 'Payment Terms', value: doc.payment_term_name || '-' },
    ],
    columns: [
      { label: '#', value: (_, index) => index + 1, type: 'number' }, { label: 'Product', value: item => getName(products, item.product_id) },
      { label: 'Description', value: item => item.description || '-' }, { label: 'MRP', value: item => Number(item.mrp || 0), type: 'number' },
      { label: 'Qty', value: item => Number(item.quantity || 0), type: 'number' }, { label: 'Unit', value: item => getName(units, item.unit_id) },
      { label: 'Price/Unit', value: item => Number(item.price_per_unit || 0), type: 'number' }, { label: 'Disc %', value: item => Number(item.discount_percentage || 0), type: 'number' },
      { label: 'Disc Amt', value: item => Number(item.discount_amount || 0), type: 'number' }, { label: 'Tax', value: item => item.tax_id ? getName(taxes, item.tax_id) : '-' },
      { label: 'Tax Amt', value: item => Number(item.tax_amount || 0), type: 'number' }, { label: 'Amount', value: item => Number(item.total_amount || 0), type: 'number' },
    ],
    rows: doc.line_items || [],
    totals: [
      { label: 'Subtotal', value: `INR ${Number(doc.subtotal || 0).toFixed(2)}`, rawValue: Number(doc.subtotal || 0) },
      { label: 'Total', value: `INR ${Number(doc.total || 0).toFixed(2)}`, rawValue: Number(doc.total || 0) },
    ],
    shareLines: [`Vendor: ${doc.vendor_name || '-'}`, `Order Date: ${formatDate(doc.order_date)}`, `Total: INR ${Number(doc.total || 0).toFixed(2)}`, `Status: ${doc.status || (doc.converted_to_bill ? 'Converted' : 'Open')}`],
  };

  return (
    <div data-testid="purchase-order-detail-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <button onClick={() => navigate('/admin/purchase/orders')} data-testid="back-btn"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-3 transition-colors print:hidden">
            <ArrowLeft className="h-4 w-4" /> Back to Purchase Orders
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Purchase Order Details</h1>
            {isVoided && <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-bold uppercase rounded" data-testid="voided-badge">Voided</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          {!doc.converted_to_bill && !isVoided ? (
            <>
            <button onClick={() => navigate(`/admin/purchase/orders/${id}/edit`)} data-testid="edit-po-btn"
              className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 font-bold tracking-wider uppercase px-5 py-3 rounded-none hover:bg-slate-50 transition-colors">
              <Pencil className="h-4 w-4" /> Edit
            </button>
            <button onClick={() => { setVoidError(''); setShowVoidModal(true); }} data-testid="void-po-btn"
              className="inline-flex items-center gap-2 border border-red-300 text-red-600 font-bold tracking-wider uppercase px-5 py-3 rounded-none hover:bg-red-50 transition-colors">
              <Ban className="h-4 w-4" /> Void PO
            </button>
            <button onClick={openConvertModal} data-testid="convert-to-bill-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
              <RefreshCw className="h-5 w-5" /> Convert to Purchase Bill
            </button>
            </>
          ) : doc.converted_to_bill ? (
            <>
            <span className="inline-flex items-center px-4 py-2 text-xs font-bold uppercase rounded bg-green-100 text-green-700" data-testid="converted-badge">
              Converted to Purchase Bill
            </span>
            {doc.purchase_bill_id && (
              <button onClick={() => navigate(`/admin/purchase/bills/${doc.purchase_bill_id}`)} data-testid="view-bill-link"
                className="text-sm font-medium text-orange-600 hover:text-orange-700 underline">
                View Bill
              </button>
            )}
            </>
          ) : null}
        </div>
      </div>

      <div className="mb-8"><DocumentExportActions config={exportConfig} testIdPrefix="purchase-order" /></div>

      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Order Number</span><span className="text-sm font-semibold text-slate-900">{doc.order_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Order Date</span><span className="text-sm text-slate-900">{formatDate(doc.order_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Due Date</span><span className="text-sm text-slate-900">{formatDate(doc.due_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Vendor</span><span className="text-sm text-slate-900">{doc.vendor_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Company</span><span className="text-sm text-slate-900">{doc.company_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Payment Terms</span><span className="text-sm text-slate-900">{doc.payment_term_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">State of Supply</span><span className="text-sm text-slate-900">{doc.state_of_supply || '-'}</span></div>
          {isVoided && <div className="col-span-2"><span className="block text-xs font-medium text-red-500 uppercase">Void Reason</span><span className="text-sm text-red-700">{doc.void_reason || '-'}</span></div>}
          {doc.description && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Description</span><span className="text-sm text-slate-900">{doc.description}</span></div>}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">MRP</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-right">Disc Amt</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                  <td className="px-4 py-3 text-slate-600">{li.description || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.mrp || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.quantity}</td>
                  <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                  <td className="px-4 py-3 text-right">{li.price_per_unit?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{li.discount_percentage}%</td>
                  <td className="px-4 py-3 text-right">{li.discount_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                  <td className="px-4 py-3 text-right">{li.tax_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">{li.total_amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span></div>
          {doc.round_off && <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{(doc.total || 0).toLocaleString()}</span></div>
        </div>
      </div>

      {/* Convert to Purchase Bill Modal */}
      {showConvert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-5xl flex flex-col" style={{ maxHeight: '90vh' }}>

            {/* Fixed Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Convert to Purchase Bill</h2>
              <button onClick={() => setShowConvert(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>

            {/* Error banner (outside columns so always visible) */}
            {convertError && (
              <div className="flex-shrink-0 flex items-center gap-2 bg-red-50 border-b border-red-200 text-red-700 px-6 py-3 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" /><span>{convertError}</span>
              </div>
            )}

            {/* 2-column scrollable body */}
            <form onSubmit={handleConvertSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="flex flex-1 min-h-0">

                {/* LEFT: Details (scrollable) */}
                <div className="flex-1 overflow-y-auto p-6 border-r border-slate-200">

                  {/* Info banner */}
                  <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-sm mb-5 text-sm">
                    Review the details below. On clicking <strong>Continue</strong>, you will be taken to the Purchase Bill creation screen with all data pre-filled.
                  </div>

                  {/* PO Summary */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">PO Summary</h4>
                      <button type="button" onClick={() => setShowAddPO(prev => !prev)} data-testid="add-po-to-conversion-btn"
                        className="inline-flex items-center gap-1 text-xs font-bold text-orange-600 hover:text-orange-700">
                        <Plus className="h-3.5 w-3.5" /> Add PO
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-sm border border-slate-200">
                      <div><span className="block text-[10px] font-medium text-slate-500 uppercase">PO Number</span><span className="text-sm font-semibold text-slate-900">{doc.order_number || '-'}</span></div>
                      <div><span className="block text-[10px] font-medium text-slate-500 uppercase">Vendor</span><span className="text-sm font-semibold text-slate-900">{doc.vendor_name || '-'}</span></div>
                      <div><span className="block text-[10px] font-medium text-slate-500 uppercase">Order Date</span><span className="text-sm text-slate-900">{formatDate(doc.order_date)}</span></div>
                      <div><span className="block text-[10px] font-medium text-slate-500 uppercase">Total</span><span className="text-sm font-bold text-slate-900">₹{(doc.total || 0).toLocaleString()}</span></div>
                    </div>
                    {showAddPO && (
                      <div className="mt-3 border border-orange-200 bg-orange-50/50 rounded-sm p-3" data-testid="additional-po-picker">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Open POs from the same vendor and company</p>
                        {eligibleAdditionalPOs.length ? (
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {eligibleAdditionalPOs.map(po => (
                              <label key={po.id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 px-3 py-2 rounded-sm cursor-pointer hover:border-orange-300">
                                <span className="flex items-center gap-2 min-w-0">
                                  <input type="checkbox" checked={additionalPoIds.includes(po.id)} onChange={() => toggleAdditionalPO(po.id)}
                                    data-testid={`additional-po-${po.id}`} className="accent-orange-500" />
                                  <span className="text-sm font-medium text-slate-900 truncate">{po.order_number || 'Unnumbered PO'}</span>
                                </span>
                                <span className="text-xs text-slate-500 whitespace-nowrap">{formatDate(po.order_date)} · ₹{(po.total || 0).toLocaleString()}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500 italic">No other open POs are available for this vendor and company.</p>
                        )}
                      </div>
                    )}
                    {additionalPoIds.length > 0 && (
                      <p className="mt-2 text-xs font-medium text-green-700">{additionalPoIds.length + 1} purchase orders included in this bill.</p>
                    )}
                  </div>

                  {/* Bill Header Fields */}
                  <div className="mb-5">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Bill Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Bill Number <span className="text-red-500">*</span></label>
                        <input type="text" value={convertData.bill_number}
                          onChange={e => setConvertData(prev => ({ ...prev, bill_number: e.target.value }))}
                          data-testid="convert-bill-number" placeholder="e.g. PB-001"
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Bill Date</label>
                        <input type="date" value={convertData.bill_date}
                          onChange={e => setConvertData(prev => ({ ...prev, bill_date: e.target.value }))}
                          data-testid="convert-bill-date"
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                      </div>
                      {/* payment_term removed from conversion modal */}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Paid Amount</label>
                        <input type="number" value={convertData.paid_amount}
                          onChange={e => setConvertData(prev => ({ ...prev, paid_amount: e.target.value }))}
                          data-testid="convert-paid-amount" min="0" step="0.01" placeholder="0"
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                      </div>
                    </div>
                  </div>

                  {/* Totals */}
                  <div>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Totals</h4>
                    <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-sm border border-slate-200">
                      <div><span className="block text-[10px] font-medium text-slate-500 uppercase mb-1">Combined PO Total</span><span className="text-base font-bold text-slate-900">₹{conversionTotal.toLocaleString()}</span></div>
                      <div><span className="block text-[10px] font-medium text-slate-500 uppercase mb-1">Balance</span><span className="text-base font-bold text-slate-900">₹{(conversionTotal - (parseFloat(convertData.paid_amount) || 0)).toLocaleString()}</span></div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Line Items (scrollable) */}
                <div className="flex-1 overflow-y-auto p-6">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                    Line Items <span className="text-slate-400 font-normal">({convertLineItems.length})</span>
                  </h4>
                  {convertLineItems.length > 0 ? (
                    <div className="flex flex-col gap-3">
                      {convertLineItems.map((li, idx) => (
                        <div key={idx} className="border border-slate-200 rounded-sm p-3 bg-white" data-testid={`convert-line-card-${idx}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-400">#{idx + 1}</span>
                              <span className="font-medium text-sm text-slate-900">{getName(products, li.product_id)}</span>
                            </div>
                            {convertLineItems.length > 1 && (
                              <button type="button" onClick={() => removeConvertLine(idx)} data-testid={`remove-convert-line-${idx}`}
                                className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <label className="block text-[10px] font-medium text-slate-500 uppercase mb-0.5">Qty</label>
                              <input type="number" value={li.quantity} onChange={e => updateConvertLine(idx, 'quantity', e.target.value)}
                                min="0" step="1" className="w-full px-2 py-1 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-slate-500 uppercase mb-0.5">Price/Unit</label>
                              <input type="number" value={li.price_per_unit} onChange={e => updateConvertLine(idx, 'price_per_unit', e.target.value)}
                                min="0" step="0.01" className="w-full px-2 py-1 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <label className="block text-[10px] font-medium text-slate-500 uppercase mb-0.5">Discount %</label>
                              <input type="number" value={li.discount_percentage} onChange={e => updateConvertLine(idx, 'discount_percentage', e.target.value)}
                                min="0" max="100" step="0.01" data-testid={`convert-discount-percent-${idx}`}
                                className="w-full px-2 py-1 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-slate-500 uppercase mb-0.5">Discount Amount</label>
                              <input type="number" value={li.discount_amount} onChange={e => updateConvertLine(idx, 'discount_amount', e.target.value)}
                                min="0" step="0.01" data-testid={`convert-discount-amount-${idx}`}
                                className="w-full px-2 py-1 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 items-end">
                            <div>
                              <label className="block text-[10px] font-medium text-slate-500 uppercase mb-0.5">Warranty</label>
                              <WarrantyDropdown
                                value={convertLineWarranties[idx]?.warranty_id || ''}
                                onChange={(wid, wname) => {
                                  const newW = [...convertLineWarranties];
                                  newW[idx] = { warranty_id: wid, warranty_name: wname || '' };
                                  setConvertLineWarranties(newW);
                                }}
                                warranties={warranties}
                                testId={`convert-warranty-${idx}`}
                              />
                            </div>
                            <div className="text-right">
                              <label className="block text-[10px] font-medium text-slate-500 uppercase mb-0.5">Amount</label>
                              <span className="text-sm font-bold text-slate-900">₹{(li.total_amount || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No line items</p>
                  )}
                </div>
              </div>

              {/* Fixed Footer */}
              <div className="flex-shrink-0 flex gap-3 justify-end px-6 py-4 border-t border-slate-200 bg-white">
                <button type="button" onClick={() => setShowConvert(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={convertSubmitting} data-testid="confirm-convert-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {convertSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showVoidModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Void Purchase Order</h2>
              <button onClick={() => setShowVoidModal(false)} disabled={voiding} className="p-1 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-4">This PO will remain in records but cannot be edited or converted to a Purchase Bill.</p>
            {voidError && <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm">{voidError}</div>}
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <textarea value={voidReason} onChange={e => setVoidReason(e.target.value)} rows={4} autoFocus
              data-testid="void-reason-input" placeholder="Why is this purchase order being voided?"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-red-400 focus:outline-none resize-none" />
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setShowVoidModal(false)} disabled={voiding} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button onClick={handleVoid} disabled={voiding || !voidReason.trim()} data-testid="confirm-void-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-red-700 disabled:opacity-50">
                {voiding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                {voiding ? 'Voiding…' : 'Void Purchase Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
